import { KeyboardEvent, SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { ActionMeta, InputActionMeta, MultiValue } from 'react-select';
import { dropRight } from 'lodash';

import { getCollectionsNew, getRecipientsListSelector } from '../../modules/selectors';
import { fetchRecipientsAction, showNotifyMessage } from '../../modules/actions';
import { Key } from '../../modules/constants/global';
// tslint:disable-next-line:no-implicit-dependencies
import validateEmail from '../../helpers/validateEmail';
import useDeliveryForm from './useDeliveryForm';

export interface IEmailItem {
  label: string;
  value: string;
}

const toEmailItem = (value: string): IEmailItem => ({ label: value, value });

const useEmailRecipients = () => {
  const dispatch = useDispatch();
  const [deliveryForm, setDeliveryForm] = useDeliveryForm();
  const recipients = useSelector(getRecipientsListSelector);
  const collection = useSelector(getCollectionsNew);
  const { collection_id: urlCollectionId = '' } = useParams<{ collection_id: string }>();

  const [openRecipientsState, setOpenRecipientsState] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [recipientsOptions, setRecipientsOptions] = useState<IEmailItem[]>([]);

  const seededCollectionIdRef = useRef<string | null>(null);
  const seenMainCustomersRef = useRef<Map<string, string>>(new Map());

  const emails = deliveryForm.recipientsEmails.map(toEmailItem);
  const setEmails = (next: readonly IEmailItem[]) =>
    setDeliveryForm({ recipientsEmails: next.map((email) => email.value.toLowerCase()) });

  useEffect(() => {
    setRecipientsOptions(recipients.map(toEmailItem));
  }, [recipients]);

  useEffect(() => {
    if (recipientsOptions.length && searchValue.length) {
      setOpenRecipientsState(true);
    }
  }, [recipientsOptions, searchValue]);

  useEffect(() => {
    if (!urlCollectionId) return;

    if (seededCollectionIdRef.current !== urlCollectionId) {
      setEmails([]);
      seenMainCustomersRef.current = new Map();

      if (collection._id !== urlCollectionId) return;

      const savedMainCustomers = (collection.mainCustomers || []).filter(
        (customer) => customer.id
      );
      if (savedMainCustomers.length) {
        setEmails(savedMainCustomers.map((customer) => toEmailItem(customer.email)));
        savedMainCustomers.forEach((customer) =>
          seenMainCustomersRef.current.set(customer.id as string, customer.email)
        );
      }
      seededCollectionIdRef.current = urlCollectionId;

      return;
    }

    if (collection._id !== urlCollectionId) return;

    const currentSaved = (collection.mainCustomers || []).filter((customer) => customer.id);
    const currentIds = new Set(currentSaved.map((customer) => customer.id as string));

    const deletedEmails: string[] = [];
    seenMainCustomersRef.current.forEach((email, id) => {
      if (!currentIds.has(id)) {
        deletedEmails.push(email);
        seenMainCustomersRef.current.delete(id);
      }
    });

    const newMainCustomers = currentSaved.filter(
      (customer) => !seenMainCustomersRef.current.has(customer.id as string)
    );
    newMainCustomers.forEach((customer) =>
      seenMainCustomersRef.current.set(customer.id as string, customer.email)
    );

    if (!deletedEmails.length && !newMainCustomers.length) return;

    const deletedSet = new Set(deletedEmails);
    const existingEmails = new Set(deliveryForm.recipientsEmails);
    const additions = newMainCustomers
      .filter((customer) => !existingEmails.has(customer.email))
      .map((customer) => toEmailItem(customer.email));

    const surviving = emails.filter((email) => !deletedSet.has(email.value));
    setEmails([...surviving, ...additions]);
  }, [urlCollectionId, collection._id, collection.mainCustomers]); // eslint-disable-line

  const onSearch = (value: string, meta: InputActionMeta) => {
    const inputSearchValue = value.replace(',', '').trim();
    if (meta.action === 'input-change') {
      setSearchValue(inputSearchValue);
      if (value.length) {
        dispatch(fetchRecipientsAction(inputSearchValue));
      }
    }
    if (meta.action === 'set-value') {
      setSearchValue(inputSearchValue);
    }
  };

  const onKeyDown = (e: KeyboardEvent) => {
    const submitKeys = new Set<string>([Key.Enter, Key.Space, Key.Comma]);
    if (submitKeys.has(e.key)) {
      const inputSearchValue = searchValue.replace(',', '').trim();
      if (!validateEmail(searchValue)) {
        dispatch(showNotifyMessage('The text should be an email type'));

        return;
      }
      const uniqEmails = emails.filter((email) => email.value !== inputSearchValue);
      uniqEmails.push(toEmailItem(inputSearchValue));
      setEmails(uniqEmails);
      setSearchValue('');
    }

    const deleteKeys = new Set<string>([Key.Backspace, Key.Delete]);
    if (deleteKeys.has(e.key) && !searchValue) {
      setEmails(dropRight(emails, 1));
    }
  };

  const onChange = (data: MultiValue<IEmailItem>, meta: ActionMeta<IEmailItem>) => {
    if (!meta || !('option' in meta) || !meta.option) return;
    setEmails(data);
  };

  const onBlur = () => {
    setRecipientsOptions([]);
    setOpenRecipientsState(false);
    if (!searchValue.length) return;

    const uniqEmails = emails.filter((email) => email.value !== searchValue);
    uniqEmails.push(toEmailItem(searchValue));
    setEmails(uniqEmails);
    setSearchValue('');
  };

  const removeEmail = (data: string, e: SyntheticEvent) => {
    e.stopPropagation();
    setEmails(emails.filter((email) => email.value !== data));
  };

  return {
    emails,
    searchValue,
    openRecipientsState,
    recipientsOptions,
    onSearch,
    onKeyDown,
    onChange,
    onBlur,
    removeEmail
  };
};

export default useEmailRecipients;
